import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  projectId: string;
}

type Msg = { id?: string; role: "user" | "assistant" | "system"; content: string };

const ProjectCoach = ({ projectId }: Props) => {
  const queryClient = useQueryClient();
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamMsgs, setStreamMsgs] = useState<Msg[] | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: history = [], isLoading } = useQuery({
    queryKey: ["project-coach", projectId],
    queryFn: async (): Promise<Msg[]> => {
      const { data, error } = await supabase
        .from("project_coach_messages")
        .select("id, role, content")
        .eq("project_id", projectId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Msg[];
    },
  });

  const messages = streamMsgs ?? history;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);

    const optimistic: Msg[] = [
      ...history,
      { role: "user", content: text },
      { role: "assistant", content: "" },
    ];
    setStreamMsgs(optimistic);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/project-coach`;
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ project_id: projectId, user_message: text }),
      });
      if (resp.status === 429) { toast.error("AI is busy. Try again in a moment."); throw new Error("rate"); }
      if (resp.status === 402) { toast.error("Workspace AI credits exhausted."); throw new Error("credits"); }
      if (!resp.ok || !resp.body) throw new Error("Stream failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, nl);
          buffer = buffer.slice(nl + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") continue;
          try {
            const parsed = JSON.parse(json);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              assistantText += delta;
              setStreamMsgs((cur) => {
                if (!cur) return cur;
                const next = [...cur];
                next[next.length - 1] = { role: "assistant", content: assistantText };
                return next;
              });
            }
          } catch { /* partial */ }
        }
      }
    } catch (e: any) {
      if (e?.message !== "rate" && e?.message !== "credits") {
        toast.error("Coach failed to respond.");
      }
    } finally {
      setStreaming(false);
      setStreamMsgs(null);
      queryClient.invalidateQueries({ queryKey: ["project-coach", projectId] });
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl flex flex-col h-[520px]">
      <div className="flex items-center gap-2 border-b border-border px-5 py-3">
        <MessageSquare className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-sm">AI Coach</h3>
        <span className="text-xs text-muted-foreground ml-auto">Scoped to this project</span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : messages.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            Ask anything about strengthening your project. Try: <em>"How do I make my budget section more competitive?"</em>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] rounded-lg px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-primary/15 text-foreground border border-primary/30"
                    : "bg-secondary/40 text-foreground border border-border"
                }`}
              >
                {m.role === "assistant" ? (
                  <div className="prose prose-sm prose-invert max-w-none [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1">
                    {m.content ? <ReactMarkdown>{m.content}</ReactMarkdown> : <Loader2 className="h-4 w-4 animate-spin" />}
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{m.content}</p>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="border-t border-border p-3 flex gap-2">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder="Ask the coach…"
          rows={2}
          className="resize-none"
          disabled={streaming}
        />
        <Button onClick={send} disabled={!input.trim() || streaming} className="self-end gap-2">
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

export default ProjectCoach;